import { FC, memo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Autocomplete, Box } from '@mui/material';
import AutocompletePaper from '@components/AutocompletePaper/AutocompletePaper';

import { StyledTextField } from '@/shared/SearchFilters/styled';
import { SearchFilter } from '@/shared/SearchFilters/types';

const DropdownSearchFilter: FC<SearchFilter> = ({ name, label, type, onChange, dependencies, options }) => {
  const { control, trigger } = useFormContext();
  const minOptionValue = useWatch({
    control,
    name: dependencies?.min ?? '',
  });
  const maxOptionValue = useWatch({
    control,
    name: dependencies?.max ?? '',
  });

  return (
    <Box>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <>
            <Autocomplete
              {...field}
              onChange={(e, v) => {
                field.onChange(v);
                if (dependencies) {
                  trigger(dependencies.min);
                  trigger(dependencies.max);
                }
                onChange();
              }}
              onInputChange={(_, data) => {
                if (data) {
                  field.onChange(data);
                  if (dependencies) {
                    trigger(dependencies.min);
                    trigger(dependencies.max);
                  }
                  onChange();
                }
              }}
              freeSolo
              getOptionLabel={(option: any) =>
                typeof option === 'object' && option !== null ? option.label || '' : String(option ?? '')
              }
              isOptionEqualToValue={(option: any, val: any) => {
                if (typeof option === 'object' && typeof val === 'object') return option?.value === val?.value;
                return String(option) === String(val);
              }}
              filterOptions={currentOptions => {
                if (!dependencies || (!minOptionValue && !maxOptionValue)) return currentOptions;

                if (!maxOptionValue) {
                  if (name === dependencies.min) {
                    return currentOptions;
                  }
                  return currentOptions.filter(option => +option > +minOptionValue);
                }

                if (maxOptionValue && minOptionValue) {
                  if (name === dependencies.min) {
                    return currentOptions.filter(option => +option < +maxOptionValue);
                  }
                  if (name === dependencies.max) {
                    return currentOptions.filter(option => +option > +minOptionValue);
                  }
                }

                if (maxOptionValue && !minOptionValue) {
                  if (name === dependencies.max) {
                    return currentOptions;
                  }
                  return currentOptions.filter(option => +option < +maxOptionValue);
                }
                return currentOptions;
              }}
              options={options as string[]}
              PaperComponent={AutocompletePaper}
              renderOption={(props, option) => {
                let renderValue =
                  typeof option === 'object' && option !== null
                    ? (option as Record<string, string>).label || String(option)
                    : String(option);

                // Format large numbers (for price/sqft numeric dropdowns)
                const numberValue = +renderValue;
                if (!Number.isNaN(numberValue) && numberValue >= 1_000) {
                  if (numberValue >= 1_000_000) {
                    renderValue = `${numberValue / 1_000_000}M`;
                  } else {
                    renderValue = `${numberValue / 1_000}K`;
                  }
                }

                return <li {...props}>{renderValue}</li>;
              }}
              value={field.value ?? ''}
              renderInput={params => (
                <StyledTextField
                  type={type || (dependencies ? 'number' : 'text')}
                  {...params}
                  label={label}
                  variant="outlined"
                />
              )}
            />
          </>
        )}
      />
    </Box>
  );
};

export default memo(DropdownSearchFilter);
